import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, MessageCircle, Linkedin, Instagram, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interests = [
  "Diseño de productos turísticos",
  "Capacitación / mentoría",
  "Desarrollo de destino",
  "Conferencia / keynote",
];

const SOCIALS = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    sub: "+52 55 4360 7330",
    icon: MessageCircle,
    href: "https://wa.me/525543607330",
  },
  {
    id: "email",
    label: "Correo",
    sub: "tony@incubatour.com",
    icon: Mail,
    href: "mailto:tony@incubatour.com",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    sub: "@tony-yarto",
    icon: Linkedin,
    href: "https://www.linkedin.com/in/tonyyarto/",
  },
  {
    id: "instagram",
    label: "Instagram",
    sub: "@tonyyarto",
    icon: Instagram,
    href: "https://instagram.com/tonyyarto",
  },
];

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    interest: interests[0],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y correo son obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("¡Solicitud enviada! Tony se pondrá en contacto contigo.");
      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        interest: interests[0],
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          "Algo salió mal. Inténtalo de nuevo en unos minutos."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-[#050505] py-28 md:py-40 border-t border-white/10 noise"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16">
        {/* Left column */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-white/40" /> Próximos programas
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display mt-6 text-4xl md:text-6xl font-black tracking-tighter uppercase leading-[0.95]"
          >
            ¿Listo para <br />
            <span className="text-white/30">transformar</span> <br /> tu destino?
          </motion.h2>
          <p className="mt-8 text-white/70 leading-relaxed max-w-md">
            Cuéntanos sobre tu proyecto. En menos de{" "}
            <strong className="text-white">48 horas</strong> recibirás una
            primera lectura estratégica y, si hay match, agendamos una sesión
            de diagnóstico sin costo.
          </p>

          <div className="mt-10 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
              O conecta directo
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.id}
                    href={s.href}
                    target={s.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`lead-social-${s.id}`}
                    className="group flex items-center gap-3 p-3 border border-white/10 bg-[#0a0a0a] hover:border-accent/50 hover:bg-accent-soft transition-all"
                  >
                    <span className="flex items-center justify-center w-8 h-8 shrink-0 border border-white/15 text-white group-hover:text-accent group-hover:border-accent transition-colors">
                      <Icon size={14} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                        {s.label}
                      </div>
                      <div className="text-[11px] text-white/85 truncate font-mono">
                        {s.sub}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
            <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 pt-3">
              <span className="h-px w-6 bg-white/30" /> México · Colombia · España
            </div>
          </div>
        </div>

        {/* Right column - form */}
        <div className="md:col-span-7">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            onSubmit={handleSubmit}
            data-testid="lead-form"
            className="bg-[#0a0a0a] border border-white/10 p-8 md:p-12"
          >
            {submitted ? (
              <div
                className="flex flex-col items-start gap-6 py-10"
                data-testid="lead-success"
              >
                <span className="font-mono text-xs tracking-widest text-white/40">
                  /01 — Enviado
                </span>
                <h3 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
                  Solicitud <br /> recibida.
                </h3>
                <p className="text-white/70 max-w-md">
                  Tony y su equipo revisarán tu información. Te contactarán en
                  las próximas 48 horas.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="lead-reset"
                  className="btn-block btn-ghost"
                >
                  Enviar otra solicitud →
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-xs tracking-widest text-white/40">
                    /Form · 01
                  </span>
                  <span className="font-display text-xs uppercase tracking-[0.25em] text-white/40">
                    Trabajemos juntos
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                  <div>
                    <label
                      htmlFor="lead-name"
                      className="text-[10px] uppercase tracking-[0.25em] text-white/50"
                    >
                      Nombre completo*
                    </label>
                    <input
                      id="lead-name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      data-testid="lead-name-input"
                      placeholder="Tu nombre"
                      className="line-input"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lead-email"
                      className="text-[10px] uppercase tracking-[0.25em] text-white/50"
                    >
                      Correo*
                    </label>
                    <input
                      id="lead-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      data-testid="lead-email-input"
                      placeholder="tu@empresa.com"
                      className="line-input"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lead-phone"
                      className="text-[10px] uppercase tracking-[0.25em] text-white/50"
                    >
                      Teléfono
                    </label>
                    <input
                      id="lead-phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      data-testid="lead-phone-input"
                      placeholder="+52 ..."
                      className="line-input"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lead-interest"
                      className="text-[10px] uppercase tracking-[0.25em] text-white/50"
                    >
                      Me interesa
                    </label>
                    <select
                      id="lead-interest"
                      name="interest"
                      value={form.interest}
                      onChange={handleChange}
                      data-testid="lead-interest-select"
                      className="line-input appearance-none cursor-pointer"
                    >
                      {interests.map((i) => (
                        <option key={i} value={i} className="bg-black">
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label
                      htmlFor="lead-message"
                      className="text-[10px] uppercase tracking-[0.25em] text-white/50"
                    >
                      Cuéntanos brevemente
                    </label>
                    <textarea
                      id="lead-message"
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      data-testid="lead-message-input"
                      placeholder="¿Cuál es el reto que quieres resolver?"
                      className="line-input resize-none"
                    />
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-[11px] text-white/40 max-w-sm">
                    Al enviar aceptas el tratamiento de tus datos para fines
                    informativos y comerciales.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-submit"
                    className="btn-block disabled:opacity-60 disabled:cursor-wait"
                  >
                    {submitting ? (
                      <>
                        Enviando <Loader2 className="animate-spin" size={14} />
                      </>
                    ) : (
                      <>
                        Enviar solicitud <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
